import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

const SimpleTaskForm = ({ isOpen, onClose, onSubmit, currentUser, users, projects }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigneeId: currentUser?.id || '',
    priority: 'important_not_urgent',
    dueDate: '',
    dueTime: '09:00', // שעת יעד ברירת מחדל
    estimate: 0,
    projectId: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit({
        ...formData,
        departmentId: currentUser.departmentId,
        createdBy: currentUser.id,
        createdAt: new Date(),
        status: 'במסלול',
        projectId: formData.projectId || null
      });
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        assigneeId: currentUser?.id || '',
        priority: 'important_not_urgent',
        dueDate: '',
        dueTime: '09:00',
        estimate: 0,
        projectId: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('שגיאה ביצירת המשימה');
    }
  };

  if (!isOpen) return null;

  // Get department users
  const departmentUsers = users.filter(u => u.departmentId === currentUser?.departmentId);
  const departmentProjects = projects.filter(p => p.departmentId === currentUser?.departmentId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-purple-600 px-4 md:px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg md:text-xl font-semibold text-white">משימה חדשה</h3>
          <button
            onClick={onClose}
            className="w-10 h-10 md:w-8 md:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 touch-manipulation"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 md:p-6">
          <div className="space-y-4">
            {/* Task Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                כותרת המשימה
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="הכנס כותרת המשימה"
                required
              />
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תיאור המשימה
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-24 resize-none"
                placeholder="תאר את המשימה בפירוט..."
              />
            </div>

            {/* Assignee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                מוקצה למי
              </label>
              <select
                name="assigneeId"
                value={formData.assigneeId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                {departmentUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                עדיפות
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="important_urgent">חשוב ודחוף</option>
                <option value="important_not_urgent">חשוב ולא דחוף</option>
                <option value="not_important_urgent">דחוף ולא חשוב</option>
                <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
              </select>
            </div>

            {/* Project */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                פרויקט (אופציונלי)
              </label>
              <select
                name="projectId"
                value={formData.projectId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">ללא פרויקט</option>
                {departmentProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Due Date, Due Time and Estimate */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  תאריך יעד
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  שעת יעד
                </label>
                <input
                  type="time"
                  name="dueTime"
                  value={formData.dueTime}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  אומדן שעות
                </label>
                <input
                  type="number"
                  name="estimate"
                  value={formData.estimate}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 min-h-[44px] touch-manipulation"
            >
              ביטול
            </button>
            <button
              type="submit"
              className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
            >
              <Plus size={16} />
              צור משימה
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SimpleTaskForm;
